<template>
  <div class="sku-spec-selector">
    <div v-if="loading" class="sku-spec-tip">规格加载中...</div>
    <div v-else-if="loadError" class="sku-spec-tip sku-spec-empty">{{ loadError }}</div>
    <div v-else-if="specGroups.length === 0" class="sku-spec-tip sku-spec-empty">
      该物品尚未配置规格（请先在「物品」中关联规格后再添加 SKU）
    </div>
    <div v-for="group in specGroups" :key="group.spec.id" class="sku-spec-group">
      <label class="sku-spec-label">{{ group.spec.name }}</label>
      <select
        class="sku-spec-select"
        :value="selected[group.spec.id] || ''"
        @change="onSelect(group.spec.id, $event.target.value)"
      >
        <option value="">请选择{{ group.spec.name }}</option>
        <option v-for="v in group.values" :key="v.id" :value="v.id">{{ v.name }}</option>
      </select>
    </div>
  </div>
</template>

<script>
import { getProductSpecs, getSpecOptions, getSpecValueOptions, getSkuSpecs } from '@/api/specs'
import { skuSpecListToValueIds } from '@/utils/skuSpec'

export default {
  name: 'SkuSpecSelector',
  props: {
    productId: { type: Number, required: true },
    value: { type: Array, default: () => [] },
    skuId: { type: Number, default: null }
  },
  data() {
    return {
      loading: false,
      loadError: '',
      productSpecs: [],
      specOptions: [],
      specValueOptions: [],
      selected: {}
    }
  },
  computed: {
    specMap() {
      const m = {}
      this.specOptions.forEach((s) => { m[s.id] = s })
      return m
    },
    specValueMap() {
      const m = {}
      this.specValueOptions.forEach((v) => { m[v.id] = v })
      return m
    },
    specGroups() {
      return this.productSpecs
        .map((ps) => ({
          spec: this.specMap[ps.spec_id],
          values: this.specValueOptions.filter((v) => v.spec_id === ps.spec_id)
        }))
        .filter((g) => g.spec)
    }
  },
  watch: {
    productId() {
      this.load()
    },
    value(val) {
      // 外部重置（如关闭弹窗后清空）时同步清空已选。
      // 注意：此处不能再次 emitValue()，否则会向外发出新的空数组引用，
      // 经 v-model 回传又触发本监听器，形成无限循环（编辑无规格 SKU 时必现）。
      if (!val || val.length === 0) {
        this.selected = {}
      }
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      if (!this.productId) return
      this.loading = true
      this.loadError = ''
      // 安全兜底：即便请求异常挂起（如代理连不上后端），也强制在 12s 后退出 loading
      const safetyTimer = setTimeout(() => {
        if (this.loading) {
          this.loading = false
          this.loadError = '规格请求超时，请检查后端服务是否在运行（api 代理目标 localhost:8090），或刷新页面重试'
        }
      }, 12000)
      try {
        const [psRes, specRes, svRes] = await Promise.all([
          getProductSpecs(this.productId),
          getSpecOptions(),
          getSpecValueOptions()
        ])
        this.productSpecs = (psRes && psRes.data && psRes.data.list) || []
        this.specOptions = (specRes && specRes.data && specRes.data.specs) || []
        this.specValueOptions = (svRes && svRes.data && svRes.data.spec_values) || []

        // 编辑已有 SKU 时，依据 skuId 预填已选规格值
        if (this.skuId && (!this.value || this.value.length === 0)) {
          const skuRes = await getSkuSpecs(this.skuId)
          const list = (skuRes && skuRes.data && skuRes.data.list) || []
          const ids = skuSpecListToValueIds(list)
          const sel = {}
          ids.forEach((id) => {
            const v = this.specValueMap[id]
            if (v) sel[v.spec_id] = id
          })
          this.selected = sel
          this.emitValue()
        }
      } catch (e) {
        this.productSpecs = []
        this.specOptions = []
        this.specValueOptions = []
        const msg = (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) || '规格加载失败，请稍后重试'
        this.loadError = msg
      } finally {
        clearTimeout(safetyTimer)
        this.loading = false
      }
    },
    onSelect(specId, val) {
      if (val) {
        this.$set(this.selected, specId, Number(val))
      } else {
        this.$delete(this.selected, specId)
      }
      this.emitValue()
    },
    emitValue() {
      const ids = Object.values(this.selected).filter(Boolean)
      this.$emit('input', ids)
    }
  }
}
</script>

<style scoped>
.sku-spec-selector {
  margin-bottom: 10px;
}
.sku-spec-tip {
  font-size: 13px;
  color: #888;
  padding: 6px 0;
}
.sku-spec-empty {
  color: #e67e22;
}
.sku-spec-group {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.sku-spec-label {
  width: 80px;
  font-size: 14px;
  color: #333;
  text-align: right;
}
.sku-spec-select {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
</style>
